/**
 * Docking layout model: a tree of splits whose leaves are tab groups, plus
 * floating groups and panels torn off into their own OS windows.
 *
 * Everything here is a pure function over plain JSON, so layouts persist as
 * they are, presets are just literals, and the operations are unit-tested.
 * Rendering lives in components/ui/layout.tsx (`DockLayout`).
 *
 * Exactly one tab group is *primary*: the editor's documents (Scene, Model,
 * Code, ...) open there when they are not docked anywhere else, and the top
 * bar's tabs act as its tab strip.
 */

export type DockDir = 'row' | 'column';
export type DropZone = 'center' | 'left' | 'right' | 'top' | 'bottom';

export interface TabsNode {
  type: 'tabs';
  id: string;
  panels: string[];
  active: string | null;
  primary?: boolean;
}

export interface SplitNode {
  type: 'split';
  id: string;
  dir: DockDir;
  children: DockNode[];
  /** Percentages, one per child, summing to 100. */
  sizes: number[];
}

export type DockNode = TabsNode | SplitNode;

export interface FloatWindow {
  id: string;
  group: TabsNode;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Where a closed panel came from, so reopening puts it back. */
export interface RestoreHint {
  /** Group it was a tab of (still there). */
  group?: string;
  /** Neighbour group it was split next to, and on which side. */
  near?: string;
  zone: DropZone;
  size?: number;
}

export interface DockState {
  version: 1;
  root: DockNode;
  floating: FloatWindow[];
  /** Panels living in their own OS window. */
  external: string[];
  /** Group shown alone (Ctrl+Space), or null. */
  maximized: string | null;
  closed: Record<string, RestoreHint>;
}

/* -------------------------------- builders -------------------------------- */

export function tabs(id: string, panels: string[], options: { active?: string; primary?: boolean } = {}): TabsNode {
  return {
    type: 'tabs',
    id,
    panels,
    active: options.active ?? panels[0] ?? null,
    ...(options.primary ? { primary: true } : {}),
  };
}

export function split(id: string, dir: DockDir, children: DockNode[], sizes?: number[]): SplitNode {
  return { type: 'split', id, dir, children, sizes: fitSizes(sizes ?? children.map(() => 100 / children.length), children.length) };
}

export function dockState(root: DockNode): DockState {
  return { version: 1, root, floating: [], external: [], maximized: null, closed: {} };
}

/* -------------------------------- queries --------------------------------- */

export function groups(node: DockNode): TabsNode[] {
  return node.type === 'tabs' ? [node] : node.children.flatMap(groups);
}

/** Every group: docked first, then floating. */
export function allGroups(state: DockState): TabsNode[] {
  return [...groups(state.root), ...state.floating.map((f) => f.group)];
}

export function findGroup(state: DockState, id: string): TabsNode | undefined {
  return allGroups(state).find((g) => g.id === id);
}

export function primaryGroup(state: DockState): TabsNode | undefined {
  return groups(state.root).find((g) => g.primary);
}

/** The group showing a panel, or null when it is closed / external. */
export function groupOf(state: DockState, panel: string): TabsNode | null {
  return allGroups(state).find((g) => g.panels.includes(panel)) ?? null;
}

export function floatOf(state: DockState, panel: string): FloatWindow | null {
  return state.floating.find((f) => f.group.panels.includes(panel)) ?? null;
}

/** Panels present anywhere in the layout (docked, floating, external). */
export function openPanels(state: DockState): string[] {
  return [...allGroups(state).flatMap((g) => g.panels), ...state.external];
}

/** Panels on screen right now: the active tab of every group (all of the maximized one's only). */
export function visiblePanels(state: DockState): string[] {
  const shown = state.maximized ? allGroups(state).filter((g) => g.id === state.maximized) : allGroups(state);
  const out = shown.map((g) => g.active).filter((p): p is string => p !== null);
  if (state.maximized) out.push(...state.floating.map((f) => f.group.active).filter((p): p is string => p !== null));
  return [...new Set([...out, ...state.external])];
}

function parentOf(node: DockNode, id: string): SplitNode | null {
  if (node.type === 'tabs') return null;
  for (const child of node.children) {
    if (child.id === id) return node;
    const found = parentOf(child, id);
    if (found) return found;
  }
  return null;
}

function nodeIds(state: DockState): Set<string> {
  const ids = new Set<string>();
  const walk = (node: DockNode) => {
    ids.add(node.id);
    if (node.type === 'split') node.children.forEach(walk);
  };
  walk(state.root);
  for (const f of state.floating) {
    ids.add(f.id);
    ids.add(f.group.id);
  }
  return ids;
}

/** A fresh node id (`g7`, `s3`, `f2`). */
export function newId(state: DockState, prefix: 'g' | 's' | 'f'): string {
  const ids = nodeIds(state);
  for (let n = ids.size + 1; ; n++) {
    const id = `${prefix}${n}`;
    if (!ids.has(id)) return id;
  }
}

/* ------------------------------- operations ------------------------------- */

const clone = (state: DockState): DockState => structuredClone(state);

/** Take a panel out of wherever it is, remembering where it was. */
export function removePanel(state: DockState, panel: string): DockState {
  const next = clone(state);
  next.external = next.external.filter((p) => p !== panel);
  const float = floatOf(next, panel);
  if (float) {
    detach(float.group, panel);
    next.closed[panel] = { zone: 'center' };
    return normalize(next);
  }
  const group = groups(next.root).find((g) => g.panels.includes(panel));
  if (!group) return normalize(next);
  if (group.panels.length > 1 || group.primary) {
    next.closed[panel] = { group: group.id, zone: 'center' };
  } else {
    const parent = parentOf(next.root, group.id);
    if (parent) {
      const index = parent.children.findIndex((c) => c.id === group.id);
      const before = parent.children[index - 1];
      const after = parent.children[index + 1];
      const sibling = before ?? after;
      const anchor = sibling ? groups(sibling)[0] : undefined;
      next.closed[panel] = {
        ...(anchor ? { near: anchor.id } : {}),
        zone: before ? (parent.dir === 'row' ? 'right' : 'bottom') : parent.dir === 'row' ? 'left' : 'top',
        size: parent.sizes[index],
      };
    }
  }
  detach(group, panel);
  return normalize(next);
}

function detach(group: TabsNode, panel: string): void {
  const index = group.panels.indexOf(panel);
  if (index < 0) return;
  group.panels.splice(index, 1);
  if (group.active === panel) group.active = group.panels[Math.min(index, group.panels.length - 1)] ?? null;
}

/**
 * Dock a panel into `target`: as a tab (`center`) or in a new group split off
 * one side. The panel is taken from wherever it was first.
 */
export function dockPanel(state: DockState, panel: string, target: string, zone: DropZone, size?: number): DockState {
  // Dropping a lone panel onto its own group is a no-op, not a removal.
  const from = groupOf(state, panel);
  if (from && from.id === target && (zone === 'center' || from.panels.length === 1)) return activatePanel(state, panel);
  const next = removePanel(state, panel);
  delete next.closed[panel];
  const group = findGroup(next, target) ?? primaryGroup(next);
  if (!group) return next;
  if (zone === 'center') {
    group.panels.push(panel);
    group.active = panel;
    return normalize(next);
  }
  // Floating groups only take tabs.
  if (next.floating.some((f) => f.group.id === group.id)) {
    group.panels.push(panel);
    group.active = panel;
    return normalize(next);
  }
  const fresh = tabs(newId(next, 'g'), [panel]);
  insertBeside(next, group.id, fresh, zone, size);
  return normalize(next);
}

function insertBeside(state: DockState, targetId: string, node: DockNode, zone: Exclude<DropZone, 'center'>, size?: number): void {
  const dir: DockDir = zone === 'left' || zone === 'right' ? 'row' : 'column';
  const after = zone === 'right' || zone === 'bottom';
  const parent = parentOf(state.root, targetId);
  if (parent && parent.dir === dir) {
    const index = parent.children.findIndex((c) => c.id === targetId);
    const share = parent.sizes[index] ?? 50;
    const mine = Math.min(share - 5, size ?? share / 2);
    parent.sizes[index] = share - mine;
    parent.children.splice(after ? index + 1 : index, 0, node);
    parent.sizes.splice(after ? index + 1 : index, 0, mine);
    return;
  }
  const mine = Math.min(90, Math.max(10, size ?? 50));
  const replace = (target: DockNode): SplitNode =>
    split(newId(state, 's'), dir, after ? [target, node] : [node, target], after ? [100 - mine, mine] : [mine, 100 - mine]);
  if (!parent) {
    state.root = replace(state.root);
    return;
  }
  const index = parent.children.findIndex((c) => c.id === targetId);
  parent.children[index] = replace(parent.children[index]!);
}

/** Put a panel in its own floating window. */
export function floatPanel(state: DockState, panel: string, rect: { x: number; y: number; w: number; h: number }): DockState {
  const next = removePanel(state, panel);
  delete next.closed[panel];
  const id = newId(next, 'f');
  next.floating.push({ id, group: tabs(`${id}-tabs`, [panel]), ...rect });
  return normalize(next);
}

/** Mark a panel as living in an OS window of its own. */
export function externalizePanel(state: DockState, panel: string): DockState {
  const next = removePanel(state, panel);
  next.external.push(panel);
  return next;
}

/** Reopen a closed panel where it was, else beside the primary group. */
export function restorePanel(state: DockState, panel: string, fallback: { near?: string; zone: DropZone; size?: number } = { zone: 'right', size: 25 }): DockState {
  if (openPanels(state).includes(panel)) return activatePanel(state, panel);
  const hint = state.closed[panel];
  const primary = primaryGroup(state)?.id;
  if (hint?.group && findGroup(state, hint.group)) return dockPanel(state, panel, hint.group, 'center');
  if (hint?.near && findGroup(state, hint.near) && hint.zone !== 'center') return dockPanel(state, panel, hint.near, hint.zone, hint.size);
  const near = fallback.near && findGroup(state, fallback.near) ? fallback.near : primary;
  if (!near) return state;
  return dockPanel(state, panel, near, fallback.zone, fallback.size);
}

/** Show a panel: select its tab and raise its floating window. */
export function activatePanel(state: DockState, panel: string): DockState {
  const group = groupOf(state, panel);
  if (!group) return state;
  const float = floatOf(state, panel);
  const raise = float && state.floating.at(-1) !== float;
  if (group.active === panel && !raise && (!state.maximized || state.maximized === group.id)) return state;
  const next = clone(state);
  const g = groupOf(next, panel)!;
  g.active = panel;
  if (float) {
    const index = next.floating.findIndex((f) => f.id === float.id);
    next.floating.push(...next.floating.splice(index, 1));
  }
  if (next.maximized && next.maximized !== g.id && !float) next.maximized = null;
  return next;
}

export function moveFloat(state: DockState, id: string, rect: Partial<Pick<FloatWindow, 'x' | 'y' | 'w' | 'h'>>): DockState {
  const next = clone(state);
  const float = next.floating.find((f) => f.id === id);
  if (float) Object.assign(float, rect);
  return next;
}

/** Resize a split's children (percentages). */
export function setSizes(state: DockState, splitId: string, sizes: readonly number[]): DockState {
  const next = clone(state);
  const walk = (node: DockNode): boolean => {
    if (node.type === 'tabs') return false;
    if (node.id === splitId) {
      node.sizes = fitSizes(sizes, node.children.length);
      return true;
    }
    return node.children.some(walk);
  };
  walk(next.root);
  return next;
}

export function toggleMaximize(state: DockState, groupId: string | null): DockState {
  const next = clone(state);
  next.maximized = groupId && next.maximized !== groupId && findGroup(next, groupId) ? groupId : null;
  return next;
}

/** Reorder a tab within its group. */
export function moveTab(state: DockState, panel: string, toIndex: number): DockState {
  const next = clone(state);
  const group = groupOf(next, panel);
  if (!group) return state;
  const from = group.panels.indexOf(panel);
  group.panels.splice(from, 1);
  group.panels.splice(Math.max(0, Math.min(toIndex, group.panels.length)), 0, panel);
  return next;
}

/* ------------------------------ normalization ----------------------------- */

function fitSizes(sizes: readonly number[], count: number): number[] {
  const raw = Array.from({ length: count }, (_, i) => {
    const v = sizes[i];
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 100 / Math.max(1, count);
  });
  const total = raw.reduce((a, b) => a + b, 0) || 1;
  return raw.map((v) => Math.round((v / total) * 100 * 1000) / 1000);
}

function normalizeNode(node: DockNode): DockNode | null {
  if (node.type === 'tabs') {
    if (node.active === null || !node.panels.includes(node.active)) node.active = node.panels[0] ?? null;
    return node.panels.length > 0 || node.primary ? node : null;
  }
  const children: DockNode[] = [];
  const sizes: number[] = [];
  node.children.forEach((child, i) => {
    const kept = normalizeNode(child);
    if (!kept) return;
    const size = node.sizes[i] ?? 100 / node.children.length;
    // A split inside a split of the same direction folds into its parent.
    if (kept.type === 'split' && kept.dir === node.dir) {
      kept.children.forEach((grand, j) => {
        children.push(grand);
        sizes.push((size * (kept.sizes[j] ?? 0)) / 100);
      });
    } else {
      children.push(kept);
      sizes.push(size);
    }
  });
  if (children.length === 0) return null;
  if (children.length === 1) return children[0]!;
  node.children = children;
  node.sizes = fitSizes(sizes, children.length);
  return node;
}

/** Drop empty groups, collapse single-child splits, keep sizes summing to 100. */
export function normalize(state: DockState): DockState {
  state.root = normalizeNode(state.root) ?? tabs('g1', [], { primary: true });
  state.floating = state.floating.filter((f) => {
    normalizeNode(f.group);
    return f.group.panels.length > 0;
  });
  if (state.maximized && !findGroup(state, state.maximized)) state.maximized = null;
  return state;
}

/* ------------------------------- persistence ------------------------------ */

export function serializeDock(state: DockState): string {
  return JSON.stringify(state);
}

function isNode(value: unknown): value is DockNode {
  if (!value || typeof value !== 'object') return false;
  const node = value as Partial<DockNode>;
  if (node.type === 'tabs')
    return typeof node.id === 'string' && Array.isArray((node as TabsNode).panels) && (node as TabsNode).panels.every((p) => typeof p === 'string');
  if (node.type === 'split')
    return (
      typeof node.id === 'string' &&
      ((node as SplitNode).dir === 'row' || (node as SplitNode).dir === 'column') &&
      Array.isArray((node as SplitNode).children) &&
      (node as SplitNode).children.every(isNode)
    );
  return false;
}

/**
 * Validate a stored layout against the panels that exist now: unknown panels
 * and duplicates are dropped, a primary group is guaranteed, and `required`
 * panels missing from it are added to the primary group. Returns null when
 * the text is not a layout at all.
 */
export function parseDock(text: string | null, known: readonly string[], required: readonly string[] = []): DockState | null {
  if (!text) return null;
  let raw: Partial<DockState>;
  try {
    raw = JSON.parse(text) as Partial<DockState>;
  } catch {
    return null;
  }
  if (!raw || raw.version !== 1 || !isNode(raw.root)) return null;
  return sanitize(
    {
      version: 1,
      root: raw.root,
      floating: Array.isArray(raw.floating) ? raw.floating.filter((f) => f && isNode(f.group) && f.group.type === 'tabs') : [],
      external: Array.isArray(raw.external) ? raw.external.filter((p) => typeof p === 'string') : [],
      maximized: typeof raw.maximized === 'string' ? raw.maximized : null,
      closed: raw.closed && typeof raw.closed === 'object' ? raw.closed : {},
    },
    known,
    required,
  );
}

export function sanitize(input: DockState, known: readonly string[], required: readonly string[] = []): DockState {
  const state = clone(input);
  const seen = new Set<string>();
  const keep = (panels: string[]) =>
    panels.filter((p) => {
      if (!known.includes(p) || seen.has(p)) return false;
      seen.add(p);
      return true;
    });
  const ids = new Set<string>();
  const walk = (node: DockNode) => {
    if (ids.has(node.id)) node.id = `${node.id}-${ids.size}`;
    ids.add(node.id);
    if (node.type === 'tabs') node.panels = keep(node.panels);
    else {
      node.sizes = fitSizes(Array.isArray(node.sizes) ? node.sizes : [], node.children.length);
      node.children.forEach(walk);
    }
  };
  walk(state.root);
  for (const f of state.floating) {
    f.group.panels = keep(f.group.panels);
    for (const k of ['x', 'y', 'w', 'h'] as const) if (typeof f[k] !== 'number' || !Number.isFinite(f[k])) f[k] = k === 'w' || k === 'h' ? 360 : 80;
  }
  // OS windows are not reopened on load: their panels go back to where they belong.
  const reopen = state.external.filter((p) => known.includes(p) && !seen.has(p));
  state.external = [];
  const docked = groups(state.root);
  let primaries = docked.filter((g) => g.primary);
  if (primaries.length > 1) primaries.slice(1).forEach((g) => delete g.primary);
  if (primaries.length === 0) {
    const first = docked[0];
    if (first) first.primary = true;
    primaries = first ? [first] : [];
  }
  normalize(state);
  let result = state;
  for (const panel of [...required.filter((p) => !seen.has(p)), ...reopen]) {
    seen.add(panel);
    result = restorePanel(result, panel, { zone: 'center' });
  }
  return result;
}
